import { useStore } from '@/hooks/useStore';
import { CloudUpload, XIcon } from 'lucide-react';
import { flowResult } from 'mobx';
import React, { useRef, useState, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Label } from '../common/Label';
import { Input } from '../common/Input';
import { cn } from '@/utils/utils';
import { FormMessage } from '../common/Form';
import { validateImageFile } from '@/utils/validate';

interface ImageUploadProps {
    filedName: string,
    required?: boolean,
    form: UseFormReturn<any>,
    caption?: string,
    className?: string,
    classNameImg?: string,
    urlImagePreview?: string,
    disabled?: boolean,
    isUploadOCR?: boolean,
    onUploadSuccess?: Function,
    isOcrNoToken?: boolean
}

const ImageUploadAccount: React.FC<ImageUploadProps> = ({
    filedName,
    // label = '',
    required = false,
    caption = '',
    form,
    className,
    classNameImg,
    urlImagePreview,
    disabled = false,
    isUploadOCR = false,
    onUploadSuccess,
    isOcrNoToken = false
}) => {
    const { setValue, trigger, formState: { errors } } = form;
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(urlImagePreview || null);

    const {
        fileStore: { uploadFile },
        accountStore: { uploadOCR },
        authStore: { uploadOcrNoToken },
        modalStore: { showAlertModal }
    } = useStore();

    // Handle image change
    const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];

        if (!file) {
            e.target.value = '';
            return;
        }

        const { isValid, error } = validateImageFile(file, t);

        if (!isValid) {
            showAlertModal({
                id: 'error',
                type: 'error',
                content: error
            });
            e.target.value = '';
            return;
        }

        const res = await flowResult(
            !isUploadOCR ?
                uploadFile({ files: file }) :
                !isOcrNoToken ? uploadOCR({ files: file }) : uploadOcrNoToken({ files: file })
        );

        if (!res?.ok) {
            onUploadSuccess && onUploadSuccess(false);
            e.target.value = '';
            return;
        }

        const newImagePreview = URL.createObjectURL(file);
        setImagePreview(newImagePreview);
        if (isUploadOCR) {
            if (res?.data?.licenseImage?.id) {
                setValue(filedName, res?.data?.licenseImage?.id);
            }
        } else if (res?.data?.length > 0) {
            setValue(filedName, res.data[0].id);
        }
        trigger(filedName);

        onUploadSuccess && onUploadSuccess(res?.data);

        e.target.value = '';
    };

    // Remove image
    const handleRemoveImage = () => {
        setImagePreview(null);
        setValue(filedName, undefined);
        trigger(filedName);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    useEffect(() => {
        if (urlImagePreview && form.getValues(filedName)) {
            setImagePreview(urlImagePreview);
        } else {
            setImagePreview(null);
        }
    }, [urlImagePreview, filedName, form]);

    useEffect(() => {
        return () => {
            if (imagePreview) {
                URL.revokeObjectURL(imagePreview);
            }
        };
    }, [imagePreview]);

    return (
        <div className='flex flex-col'>
            <Input
                type='file'
                accept='image/*'
                ref={fileInputRef}
                onChange={handleImageChange}
                className='hidden'
            />
            {imagePreview ?
                (
                    <div className={cn(
                        'relative h-fit w-full border border-input rounded-lg',
                        !!errors[filedName] && 'border-primaryWeb',
                        className
                    )}
                    >
                        <img
                            src={imagePreview}
                            alt='Preview'
                            className={cn('h-48 w-full object-contain rounded-lg', classNameImg)}
                        />
                        {caption && (
                            <div className='absolute bottom-0 left-0 flex items-center justify-center bg-background-12 w-full rounded-b-md h-8'>
                                <Label required={required} className='line-clamp-1 p-1 text-fs-mid14 leading-custom-normal font-normal'>
                                    {caption}
                                </Label>
                            </div>
                        )}
                        {!disabled && (
                            <button
                                type='button'
                                title='Remove image'
                                onClick={handleRemoveImage}
                                className='absolute top-1 right-1 bg-black bg-opacity-50 rounded-full p-1 shadow-lg'
                            >
                                <XIcon className='w-3 h-3 text-white' />
                            </button>
                        )}
                    </div>
                ) :
                (
                    <button
                        type='button'
                        disabled={disabled}
                        onClick={() => fileInputRef.current?.click()}
                        className={cn(
                            'w-full h-48 flex flex-col items-center justify-center rounded-lg border border-input transition-colors duration-300',
                            !!errors[filedName] && 'border-destructive',
                            disabled ? 'cursor-not-allowed' : 'hover:border-primaryWeb',
                            className
                        )}
                    >
                        <div className='flex flex-col items-center justify-center h-48'>
                            <CloudUpload className='size-10 text-primaryWeb' />
                            <p className='mt-1 text-placeholder'>{t('field.choose_image')}</p>
                        </div>
                        {caption && (
                            <div className='flex items-center justify-center bg-background-12 w-full rounded-b-lg h-8'>
                                <Label required={required} className='line-clamp-1 p-1 text-fs-mid14 leading-custom-normal font-normal'>
                                    {caption}
                                </Label>
                            </div>
                        )}
                    </button>
                )}
            {errors[filedName] && (
                <FormMessage className='mt-0.5'>
                    {errors[filedName]?.message as string}
                </FormMessage>
            )}
        </div>
    );
};

export default ImageUploadAccount;
